/**
 * Bulk transaction import: upload → map columns → map categories → review → drafts.
 *
 * The staged shape is the point. An accountant's export is not a set of approved project costs,
 * so the wizard ends by creating *drafts* and handing the admin straight to the review queue.
 * Nothing it does can move a project's actuals on its own.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { UploadSimple, Download, CheckCircle, Warning, XCircle, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { api } from '../../../context/AuthContext';
import { cn } from '../../../lib/utils';
import { money } from './financeFormat';

const STEPS = ['Upload', 'Map columns', 'Map categories', 'Review'];
const NONE = '__none';

const StatusIcon = ({ status }) =>
  status === 'error' ? <XCircle className="h-4 w-4 text-red-600" weight="fill" />
    : status === 'warning' ? <Warning className="h-4 w-4 text-amber-600" weight="fill" />
      : <CheckCircle className="h-4 w-4 text-emerald-600" weight="fill" />;

export const ImportWizardDialog = ({ open, onOpenChange, projectId, projectType, onImported }) => {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null);
  const [batch, setBatch] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [categoryMapping, setCategoryMapping] = useState({});
  const [validation, setValidation] = useState(null);
  const [rowFilter, setRowFilter] = useState('all');
  const [corrections, setCorrections] = useState({});
  const [saveAs, setSaveAs] = useState('');
  const [savedMappings, setSavedMappings] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(0); setBatch(null); setValidation(null); setResult(null);
    setCorrections({}); setCategoryMapping({}); setSaveAs('');
    api.get('/admin/v2/finance/import/template/meta', { params: { project_type: projectType } })
      .then((res) => setMeta(res.data)).catch(() => {});
    api.get('/admin/v2/finance/import/mappings')
      .then((res) => setSavedMappings(res.data.mappings || [])).catch(() => {});
  }, [open, projectType]);

  const downloadTemplate = async () => {
    const res = await api.get('/admin/v2/finance/import/template', { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'flippex-transaction-import-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post(`/admin/v2/finance/projects/${projectId}/import/upload`, form);
      setBatch(res.data);
      setColumnMapping(res.data.column_mapping || {});
      setStep(1);
      toast.success(`${res.data.row_count} rows detected in ${res.data.filename}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read that file');
    } finally {
      setBusy(false);
    }
  };

  const runValidation = async (extra = {}) => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/v2/finance/import/${batch.id}/validate`, {
        column_mapping: columnMapping,
        category_mapping: categoryMapping,
        corrections,
        ...extra,
      });
      setValidation(res.data);
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validation failed');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const goToCategories = async () => {
    const data = await runValidation();
    if (data) setStep(2);
  };

  const goToReview = async () => {
    if (saveAs.trim()) {
      await api.post('/admin/v2/finance/import/mappings', {
        name: saveAs.trim(),
        column_mapping: columnMapping,
        category_mapping: categoryMapping,
      }).catch(() => {});
      toast.success(`Mapping saved as "${saveAs.trim()}"`);
    }
    const data = await runValidation();
    if (data) setStep(3);
  };

  const correctRow = (index, field, value) => {
    setCorrections((prev) => ({ ...prev, [index]: { ...(prev[index] || {}), [field]: value } }));
  };

  const commit = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/v2/finance/import/${batch.id}/commit`, { skip_duplicates: true });
      setResult(res.data);
      setStep(4);
      onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const summary = validation?.summary;
  const rows = useMemo(() => {
    const all = validation?.rows || [];
    if (rowFilter === 'all') return all;
    if (rowFilter === 'duplicates') return all.filter((r) => r.duplicate);
    return all.filter((r) => r.status === rowFilter);
  }, [validation, rowFilter]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="import-wizard">
        <DialogHeader>
          <DialogTitle>Import transactions</DialogTitle>
        </DialogHeader>

        {step < 4 && (
          <div className="flex items-center gap-2 text-xs" data-testid="import-steps">
            {STEPS.map((label, index) => (
              <React.Fragment key={label}>
                <span
                  className={cn(
                    'px-2.5 py-1 rounded-full',
                    index === step ? 'bg-primary text-white font-medium'
                      : index < step ? 'bg-emerald-50 text-emerald-700' : 'bg-black/[0.05] text-foreground-muted'
                  )}
                >
                  {index + 1}. {label}
                </span>
                {index < STEPS.length - 1 && <span className="text-foreground-muted">→</span>}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Step 1 - upload */}
        {step === 0 && (
          <div className="space-y-4 py-2">
            <label
              className="flex flex-col items-center justify-center gap-2 py-12 border-2 border-dashed border-black/[0.12] rounded-xl cursor-pointer hover:border-primary/40 hover:bg-black/[0.01] transition-colors"
              data-testid="import-dropzone"
            >
              <UploadSimple className="h-8 w-8 text-foreground-muted" />
              <span className="text-sm font-medium">Drop a CSV or Excel file here, or browse</span>
              <span className="text-xs text-foreground-muted">.csv or .xlsx · up to 10MB</span>
              <input
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                disabled={busy}
                onChange={(e) => upload(e.target.files?.[0])}
                data-testid="import-file-input"
              />
            </label>

            <div className="flex items-center justify-between gap-4">
              <button
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                onClick={downloadTemplate}
                data-testid="import-download-template"
              >
                <Download className="h-4 w-4" />
                Download the Flippex import template
              </button>
              {savedMappings.length > 0 && (
                <Select
                  onValueChange={(name) => {
                    const saved = savedMappings.find((m) => m.name === name);
                    if (saved) {
                      setColumnMapping(saved.column_mapping || {});
                      setCategoryMapping(saved.category_mapping || {});
                      toast.success(`Loaded "${name}" mapping - upload your file to apply it`);
                    }
                  }}
                >
                  <SelectTrigger className="w-[220px] h-9" data-testid="import-saved-mapping">
                    <SelectValue placeholder="Use a saved mapping" />
                  </SelectTrigger>
                  <SelectContent>
                    {savedMappings.map((m) => (
                      <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {meta && (
              <div className="text-xs text-foreground-muted border-t border-black/[0.06] pt-3">
                <p className="font-medium text-foreground mb-1">Required columns</p>
                <p>
                  {meta.columns.filter((c) => c.required).map((c) => c.label).join(' · ')}
                  {' — everything else is optional and can be mapped from your own column names.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 2 - column mapping */}
        {step === 1 && batch && (
          <div className="space-y-3 py-2">
            <p className="text-sm text-foreground-muted">
              {batch.row_count} rows found in <strong>{batch.filename}</strong>. We matched your
              columns to Flippex fields — adjust anything we got wrong.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(meta?.columns || []).map((column) => (
                <div key={column.field} className="flex items-center gap-2">
                  <Label className="w-[46%] text-xs">
                    {column.label}
                    {column.required && <span className="text-red-600"> *</span>}
                  </Label>
                  <Select
                    value={columnMapping[column.field] || NONE}
                    onValueChange={(v) =>
                      setColumnMapping((prev) => {
                        const next = { ...prev };
                        if (v === NONE) delete next[column.field];
                        else next[column.field] = v;
                        return next;
                      })
                    }
                  >
                    <SelectTrigger className="flex-1 h-9" data-testid={`import-map-${column.field}`}>
                      <SelectValue placeholder="Not mapped" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not mapped</SelectItem>
                      {(batch.headers || []).map((header) => (
                        <SelectItem key={header} value={header}>{header}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
              <Button onClick={goToCategories} disabled={busy} data-testid="import-to-categories">
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 - category mapping */}
        {step === 2 && (
          <div className="space-y-3 py-2">
            <p className="text-sm text-foreground-muted">
              Map the categories from your file onto Flippex categories. Anything left unmapped is
              kept as-is.
            </p>
            <div className="space-y-2 max-h-[46vh] overflow-y-auto">
              {(validation?.source_categories || []).length === 0 ? (
                <p className="text-sm text-foreground-muted py-4">
                  No categories found in the file — entries will be categorised from their account.
                </p>
              ) : (
                validation.source_categories.map((source) => (
                  <div key={source} className="flex items-center gap-3">
                    <span className="w-[42%] text-sm truncate" title={source}>{source}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-foreground-muted" />
                    <Select
                      value={categoryMapping[source] || NONE}
                      onValueChange={(v) =>
                        setCategoryMapping((prev) => {
                          const next = { ...prev };
                          if (v === NONE) delete next[source];
                          else next[source] = v;
                          return next;
                        })
                      }
                    >
                      <SelectTrigger className="flex-1 h-9" data-testid={`import-category-${source}`}>
                        <SelectValue placeholder="Keep as-is" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Keep as-is</SelectItem>
                        {(validation?.flippex_categories || []).map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))
              )}
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-black/[0.06]">
              <Checkbox
                checked={saveAs !== ''}
                onCheckedChange={(checked) => setSaveAs(checked ? (batch?.filename?.split('.')[0] || 'My mapping') : '')}
                data-testid="import-save-mapping-toggle"
              />
              <span className="text-xs text-foreground-muted">Remember this mapping as</span>
              {saveAs !== '' && (
                <Input
                  value={saveAs}
                  onChange={(e) => setSaveAs(e.target.value)}
                  className="h-8 w-[220px]"
                  placeholder="e.g. Xero export"
                  data-testid="import-save-mapping-name"
                />
              )}
              <div className="ml-auto flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button onClick={goToReview} disabled={busy} data-testid="import-to-review">Continue</Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4 - review */}
        {step === 3 && summary && (
          <div className="space-y-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              {[
                ['all', `All ${summary.total}`],
                ['ready', `✓ ${summary.ready} ready`],
                ['warning', `⚠ ${summary.warnings} to review`],
                ['error', `✕ ${summary.errors} errors`],
                ['duplicates', `${summary.duplicates} duplicates`],
              ].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setRowFilter(key)}
                  className={cn(
                    'px-2.5 py-1 rounded-full text-xs',
                    rowFilter === key ? 'bg-primary text-white' : 'bg-black/[0.05] hover:bg-black/[0.08]'
                  )}
                  data-testid={`import-filter-${key}`}
                >
                  {label}
                </button>
              ))}
              <span className="ml-auto text-xs text-foreground-muted">
                {summary.importable} importable · {money(summary.total_amount)}
              </span>
            </div>

            {summary.errors > 0 && (
              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                Rows with errors are skipped unless you fix them here — edit the highlighted field
                and the row revalidates.
              </p>
            )}

            <div className="border border-black/[0.06] rounded-lg overflow-hidden max-h-[44vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-black/[0.03] sticky top-0">
                  <tr>
                    {['', 'Date', 'Description', 'Amount', 'Type', 'Category', 'Phase', 'Account', 'Issue'].map((h) => (
                      <th key={h} className="py-2 px-2 text-left font-semibold uppercase tracking-wide text-[10px] text-foreground-muted">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.index} className="border-t border-black/[0.04]" data-testid={`import-row-${row.index}`}>
                      <td className="py-1.5 px-2"><StatusIcon status={row.status} /></td>
                      <td className="py-1.5 px-2">
                        {row.errors?.some((e) => e.includes('date')) ? (
                          <Input
                            type="date"
                            className="h-7 w-[130px] text-xs"
                            onChange={(e) => correctRow(row.index, 'entry_date', e.target.value)}
                            data-testid={`import-fix-date-${row.index}`}
                          />
                        ) : row.entry_date}
                      </td>
                      <td className="py-1.5 px-2 max-w-[220px]">
                        {row.errors?.some((e) => e.includes('Description')) ? (
                          <Input
                            className="h-7 text-xs"
                            placeholder="Describe this transaction"
                            onChange={(e) => correctRow(row.index, 'description', e.target.value)}
                            data-testid={`import-fix-description-${row.index}`}
                          />
                        ) : <span className="truncate block">{row.description}</span>}
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums whitespace-nowrap">
                        {row.amount === null ? (
                          <Input
                            className="h-7 w-[90px] text-xs"
                            onChange={(e) => correctRow(row.index, 'amount', e.target.value)}
                            data-testid={`import-fix-amount-${row.index}`}
                          />
                        ) : money(row.amount, { decimals: 2 })}
                      </td>
                      <td className="py-1.5 px-2 capitalize">{row.entry_type}</td>
                      <td className="py-1.5 px-2">{row.category || '—'}</td>
                      <td className="py-1.5 px-2">{row.phase || '—'}</td>
                      <td className="py-1.5 px-2 text-foreground-muted">{row.account_code}</td>
                      <td className="py-1.5 px-2 text-foreground-muted max-w-[220px]">
                        <span className="truncate block" title={[...(row.errors || []), ...(row.warnings || [])].join('; ')}>
                          {[...(row.errors || []), ...(row.warnings || [])][0] || ''}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => runValidation()} disabled={busy} data-testid="import-revalidate">
                Revalidate
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <Button onClick={commit} disabled={busy || summary.importable === 0} data-testid="import-commit">
                  Import {summary.importable - summary.duplicates} draft entries
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Done */}
        {step === 4 && result && (
          <div className="py-6 text-center space-y-4" data-testid="import-done">
            <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto" weight="duotone" />
            <div>
              <p className="text-base font-medium">{result.created} transactions imported as drafts</p>
              <p className="text-sm text-foreground-muted mt-1">
                {money(result.total_amount)} staged. Review and approve them before they are
                included in project actuals.
              </p>
              {result.skipped_duplicates > 0 && (
                <p className="text-xs text-amber-700 mt-2">
                  {result.skipped_duplicates} duplicate row{result.skipped_duplicates === 1 ? '' : 's'} skipped
                </p>
              )}
              {result.skipped_errors > 0 && (
                <p className="text-xs text-red-700 mt-1">
                  {result.skipped_errors} row{result.skipped_errors === 1 ? '' : 's'} skipped because of errors
                </p>
              )}
              <Badge variant="outline" className="mt-3 text-xs">Batch {result.batch_code}</Badge>
            </div>
            <Button onClick={() => { onOpenChange(false); onImported?.('review'); }} data-testid="import-review-drafts">
              Review drafts <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportWizardDialog;
